const chapter = {
  id: 'moving-charges-magnetism',
  slug: 'moving-charges-magnetism',
  subject: 'physics',
  subjectLabel: 'Physics',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Moving Charges & Magnetism',
        hindi: 'गतिमान आवेश और चुम्बकत्व',
  },
  estReadMinutes: 65,
  difficulty: {
    english: 'Hard',
        hindi: 'कठिन',
  },
  weightage: {
    english: '2–3 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/एनईईटी में 2-3 प्रश्न',
  },
  syllabusTags: ['Electromagnetism', 'JEE-Main', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'lorentz',
      title: {
        english: 'Magnetic Force on Charges & Currents',
        hindi: 'आवेशों और धाराओं पर चुम्बकीय बल',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Lorentz force',
        hindi: 'लोरेंत्ज़ बल',
          },
          statement: {
            english: `On a charge $q$ moving with velocity $\\vec v$ in fields $\\vec E, \\vec B$:
$$\\vec F = q(\\vec E + \\vec v \\times \\vec B).$$
Magnetic part does **no work** ($\\vec F_B \\perp \\vec v$).`,
            hindi: 'चार्ज पर $q$ वेग $\\vec v$ के साथ फ़ील्ड $\\vec E, \\vec B$ में घूम रहा है: $$\\vec F = q(\\vec E + \\vec v \\times \\vec B).$$ चुंबकीय भाग **कोई काम** नहीं करता ($\\vec F_B \\perp \\vec v$)।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Charged particle in uniform $\\vec B$ ($\\vec v \\perp \\vec B$): circular motion of radius $r = mv/(qB)$, period $T = 2\\pi m/(qB)$ (independent of speed). Frequency $f_c = qB/(2\\pi m)$ — **cyclotron frequency**.',
        hindi: 'एकसमान $\\vec B$ ($\\vec v \\perp \\vec B$) में आवेशित कण: त्रिज्या $r = mv/(qB)$, अवधि $T = 2\\pi m/(qB)$ (गति से स्वतंत्र) की गोलाकार गति। आवृत्ति $f_c = qB/(2\\pi m)$ - **साइक्लोट्रॉन आवृत्ति**।',
            },
            {
              english: '$\\vec v$ at angle to $\\vec B$: helical path. Pitch $= v_\\parallel T = (2\\pi m v\\cos\\theta)/(qB)$.',
        hindi: '$\\vec v$ $\\vec B$ के कोण पर: पेचदार पथ। पिच $= v_\\parallel T = (2\\pi m v\\cos\\theta)/(qB)$.',
            },
            {
              english: '**Force on current-carrying conductor** in field: $\\vec F = I\\vec L \\times \\vec B$ (straight wire), $d\\vec F = I\\,d\\vec\\ell \\times \\vec B$.',
        hindi: '**करंट ले जाने वाले कंडक्टर पर बल** फ़ील्ड में: $\\vec F = I\\vec L \\times \\vec B$ (सीधा तार), $d\\vec F = I\\,d\\vec\\ell \\times \\vec B$।',
            },
            {
              english: '**Velocity selector**: $E = vB$ ⇒ undeflected.',
        hindi: '**वेग चयनकर्ता**: $E = vB$ ⇒ अविक्षेपित।',
            },
          ],
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'A proton ($m = 1.67\\times 10^{-27}$ kg) enters a 0.5 T field perpendicularly at $10^6$ m/s. Radius of circular path?',
        hindi: 'एक प्रोटॉन ($m = 1.67\\times 10^{-27}$ किग्रा) $10^6$ m/s पर लंबवत रूप से 0.5 T क्षेत्र में प्रवेश करता है। वृत्ताकार पथ की त्रिज्या?',
          },
          solution: {
            english: '$r = mv/(qB) = (1.67\\times 10^{-27})(10^6)/((1.6\\times 10^{-19})(0.5)) \\approx 0.0209$ m ≈ 2.1 cm.',
        hindi: '$r = mv/(qB) = (1.67\\times 10^{-27})(10^6)/((1.6\\times 10^{-19})(0.5)) \\approx 0.0209$ मी ≈ 2.1 सेमी.',
          },
        },
      ],
    },
    {
      id: 'biot-savart',
      title: {
        english: 'Biot–Savart Law & Standard Fields',
        hindi: 'बायो-सावर्ट नियम और मानक क्षेत्र',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Biot–Savart law',
        hindi: 'बायोट-सावर्ट कानून',
          },
          statement: {
            english: '$d\\vec B = \\dfrac{\\mu_0}{4\\pi}\\dfrac{I\\,d\\vec\\ell \\times \\hat r}{r^2}$, $\\mu_0 = 4\\pi\\times 10^{-7}$ T·m/A.',
        hindi: '$d\\vec B = \\dfrac{\\mu_0}{4\\pi}\\dfrac{I\\,d\\vec\\ell \\times \\hat r}{r^2}$, $\\mu_0 = 4\\pi\\times 10^{-7}$ T·m/A.',
          },
        },
        {
          type: 'table',
          headers: ['Configuration', '$B$', 'Notes'],
          rows: [
            ['Infinite straight wire (perpendicular distance $r$)', '$B = \\mu_0 I/(2\\pi r)$', 'circular field lines'],
            ['Finite wire (angles $\\theta_1, \\theta_2$ at point)', '$B = \\dfrac{\\mu_0 I}{4\\pi r}(\\sin\\theta_1 + \\sin\\theta_2)$', ''],
            ['Centre of circular loop, radius $R$', '$B = \\mu_0 I/(2R)$', 'axial direction'],
            ['Axis of loop, distance $x$', '$B = \\dfrac{\\mu_0 I R^2}{2(R^2+x^2)^{3/2}}$', ''],
            ['Long solenoid (centre)', '$B = \\mu_0 n I$', '$n$ = turns per metre'],
            ['Toroid (mean radius $r$)', '$B = \\mu_0 N I/(2\\pi r)$', 'inside; 0 outside'],
          ],
        },
      ],
    },
    {
      id: 'ampere',
      title: {
        english: 'Ampère\'s Circuital Law',
        hindi: 'एम्पीयर का परिपथीय नियम',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Ampère\'s law',
        hindi: 'एम्पीयर का नियम',
          },
          statement: {
            english: '$\\displaystyle\\oint \\vec B \\cdot d\\vec\\ell = \\mu_0 I_{\\text{enc}}$ for any closed Amperian loop.',
        hindi: 'किसी भी बंद एम्पेरियन लूप के लिए $\\displaystyle\\oint \\vec B \\cdot d\\vec\\ell = \\mu_0 I_{\\text{enc}}$।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Use symmetric Amperian loops (circle around wire, rectangle for solenoid).',
        hindi: 'सममित एम्पेरियन लूप का उपयोग करें (तार के चारों ओर घेरा, सोलनॉइड के लिए आयत)।',
            },
            {
              english: 'For a long solid cylindrical wire (radius $R$) carrying uniform current $I$: $B = \\mu_0 I r/(2\\pi R^2)$ for $r < R$, $\\mu_0 I/(2\\pi r)$ for $r > R$.',
        hindi: 'एक समान धारा ले जाने वाले लंबे ठोस बेलनाकार तार (त्रिज्या $R$) के लिए $I$: $r < R$ के लिए $B = \\mu_0 I r/(2\\pi R^2)$, $r > R$ के लिए $\\mu_0 I/(2\\pi r)$।',
            },
          ],
        },
      ],
    },
    {
      id: 'parallel-wires',
      title: {
        english: 'Force Between Parallel Currents',
        hindi: 'समान्तर धाराओं के बीच बल',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Force per unit length',
        hindi: 'प्रति इकाई लंबाई पर बल *-टर्न लूप का **चुंबकीय क्षण**: (दिशा के लिए दाएं हाथ का नियम)।',
          },
          statement: {
            english: `Two long parallel wires carrying $I_1, I_2$ separated by $d$ exert on each other:
$$\\dfrac{F}{L} = \\dfrac{\\mu_0 I_1 I_2}{2\\pi d}.$$
**Attractive** if same direction, **repulsive** if opposite. (Defines the ampere historically.)`,
            hindi: '$I_1, I_2$ को $d$ से अलग करने वाले दो लंबे समानांतर तार एक दूसरे पर दबाव डालते हैं: $$\\dfrac{F}{L} = \\dfrac{\\mu_0 I_1 I_2}{2\\pi d}.$$ **आकर्षक** यदि समान दिशा हो, **प्रतिकारक** यदि विपरीत हो। (एम्पीयर को ऐतिहासिक रूप से परिभाषित करता है।)',
          },
        },
      ],
    },
    {
      id: 'loop-dipole',
      title: {
        english: 'Current Loop as a Magnetic Dipole',
        hindi: 'चुम्बकीय द्विध्रुव के रूप में धारा पाश',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Magnetic moment** of $N$-turn loop: $\\vec m = N I \\vec A$ (right-hand rule for direction).',
        hindi: '$N$-टर्न लूप का **चुंबकीय क्षण**: $\\vec m = N I \\vec A$ (दिशा के लिए दाएं हाथ का नियम)।',
            },
            {
              english: 'Axial field (far): $B = \\mu_0 (2m)/(4\\pi x^3)$. Equatorial: $B = \\mu_0 m/(4\\pi x^3)$. (Compare electric dipole.)',
        hindi: 'अक्षीय क्षेत्र (दूर): $B = \\mu_0 (2m)/(4\\pi x^3)$. भूमध्यरेखीय: $B = \\mu_0 m/(4\\pi x^3)$. (विद्युत द्विध्रुव की तुलना करें।)',
            },
            {
              english: '**Torque on loop** in uniform $\\vec B$: $\\vec\\tau = \\vec m \\times \\vec B$. PE: $U = -\\vec m \\cdot \\vec B$.',
        hindi: '**लूप पर टॉर्क** एकसमान $\\vec B$: $\\vec\\tau = \\vec m \\times \\vec B$ में। पीई: $U = -\\vec m \\cdot \\vec B$.',
            },
            {
              english: 'Net **force** on a closed loop in **uniform** field is zero.',
        hindi: '**यूनिफ़ॉर्म** फ़ील्ड में एक बंद लूप पर नेट **बल** शून्य है।',
            },
          ],
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'A circular loop of radius 5 cm carrying 2 A. Magnetic moment.',
        hindi: '5 सेमी त्रिज्या का एक गोलाकार लूप जिसमें 2 A. चुंबकीय क्षण है।',
          },
          solution: {
            english: '$m = IA = 2 \\cdot \\pi(0.05)^2 = 5\\pi\\times 10^{-3} \\approx 0.0157$ A·m².',
        hindi: '$m = IA = 2 \\cdot \\pi(0.05)^2 = 5\\pi\\times 10^{-3} \\approx 0.0157$ A·m².',
          },
        },
      ],
    },
    {
      id: 'galvanometer',
      title: {
        english: 'Moving-Coil Galvanometer',
        hindi: 'चल-कुण्डली गैल्वेनोमीटर',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'A coil of $N$ turns, area $A$, in radial magnetic field $B$, suspended by torsional spring of stiffness $k$. At equilibrium, magnetic torque = restoring torque:',
        hindi: '$N$ घुमावों की एक कुंडली, क्षेत्रफल $A$, रेडियल चुंबकीय क्षेत्र $B$ में, कठोरता वाले टॉर्सनल स्प्रिंग $k$ द्वारा निलंबित है। संतुलन पर, चुंबकीय torque = restoring torque:',
          },
        },
        {
          type: 'equation',
          latex: 'NIAB = k\\phi \\quad\\Rightarrow\\quad I = \\dfrac{k}{NAB}\\,\\phi.',
        },
        {
          type: 'list',
          items: [
            {
              english: '**Current sensitivity** $\\phi/I = NAB/k$.',
        hindi: '**वर्तमान संवेदनशीलता** $\\phi/I = NAB/k$.',
            },
            {
              english: '**Voltage sensitivity** $\\phi/V = NAB/(kR_g)$.',
        hindi: '**वोल्टेज संवेदनशीलता** $\\phi/V = NAB/(kR_g)$.',
            },
            {
              english: 'Galvanometer → **ammeter**: shunt $S = G I_g/(I - I_g)$ in parallel.',
        hindi: 'गैल्वेनोमीटर → **एमीटर**: समानांतर में शंट $S = G I_g/(I - I_g)$।',
            },
            {
              english: 'Galvanometer → **voltmeter**: high resistance $R = V/I_g - G$ in series.',
        hindi: 'गैल्वेनोमीटर → **वोल्टमीटर**: श्रृंखला में उच्च प्रतिरोध $R = V/I_g - G$।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\vec F = q(\\vec E + \\vec v\\times\\vec B)',
      desc: {
        english: 'Lorentz',
        hindi: 'लोरेंत्ज़',
      },
    },
    {
      latex: 'r = mv/(qB),\\ T = 2\\pi m/(qB)',
      desc: {
        english: 'Cyclotron',
        hindi: 'साइक्लोट्रॉन',
      },
    },
    {
      latex: 'B = \\mu_0 I/(2\\pi r)',
      desc: {
        english: 'Long wire',
        hindi: 'लंबा तार',
      },
    },
    {
      latex: 'B_{\\text{loop centre}} = \\mu_0 I/(2R)',
      desc: {
        english: 'Loop',
        hindi: 'लूप',
      },
    },
    {
      latex: 'B_{\\text{sol}} = \\mu_0 n I',
      desc: {
        english: 'Solenoid',
        hindi: 'सोलेनॉइड',
      },
    },
    {
      latex: '\\vec\\tau = \\vec m\\times\\vec B',
      desc: {
        english: 'Loop torque',
        hindi: 'लूप टॉर्क',
      },
    },
    {
      latex: 'F/L = \\mu_0 I_1 I_2/(2\\pi d)',
      desc: {
        english: 'Parallel wires',
        hindi: 'समानांतर तार',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Cyclotron',
      problem: {
        english: 'Cyclotron frequency of an electron in a 1 T field. ($e/m = 1.76\\times 10^{11}$)',
        hindi: '1 टी क्षेत्र में एक इलेक्ट्रॉन की साइक्लोट्रॉन आवृत्ति। ($e/m = 1.76\\times 10^{11}$)',
      },
      answer: {
        english: '$\\approx 2.8\\times 10^{10}$ Hz',
        hindi: '$\\approx 2.8\\times 10^{10}$ हर्ट्ज',
      },
      solution: {
        english: '$f_c = eB/(2\\pi m) = 1.76\\times 10^{11}/(2\\pi)$.',
        hindi: '$f_c = eB/(2\\pi m) = 1.76\\times 10^{11}/(2\\pi)$.',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Solenoid',
      problem: {
        english: 'Magnetic field at the centre of a solenoid with 500 turns/m carrying 4 A.',
        hindi: '500 turns/m के साथ 4 ए ले जाने वाली सोलनॉइड के केंद्र में चुंबकीय क्षेत्र।',
      },
      answer: {
        english: '$2.51\\times 10^{-3}$ T ≈ 2.5 mT',
        hindi: '$2.51\\times 10^{-3}$ टी ≈ 2.5 एमटी',
      },
      solution: {
        english: '$B = \\mu_0 n I = 4\\pi\\times 10^{-7}\\cdot 500\\cdot 4$.',
        hindi: '$B = \\mu_0 n I = 4\\pi\\times 10^{-7}\\cdot 500\\cdot 4$.',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Wire force',
      problem: {
        english: 'Two parallel wires 1 m apart carrying 5 A and 10 A in same direction. Force per metre?',
        hindi: 'दो समानांतर तार 1 मीटर की दूरी पर 5 ए और 10 ए को एक ही दिशा में ले जा रहे हैं। बल प्रति मीटर?',
      },
      answer: {
        english: '$10^{-5}$ N/m, attractive',
        hindi: '$10^{-5}$ एन/एम, आकर्षक',
      },
      solution: {
        english: '$F/L = \\mu_0(5)(10)/(2\\pi)$.',
        hindi: '$F/L = \\mu_0(5)(10)/(2\\pi)$.',
      },
    },
    {
      year: 2021,
      exam: 'JEE Main',
      tag: 'Loop',
      problem: {
        english: 'Magnetic field at the centre of a circular loop of radius 10 cm carrying 5 A.',
        hindi: '10 सेमी त्रिज्या वाले एक वृत्ताकार लूप के केंद्र पर चुंबकीय क्षेत्र, 5 ए.',
      },
      answer: {
        english: '$\\pi \\times 10^{-5}$ T ≈ 3.14 × 10⁻⁵ T',
        hindi: '$\\pi \\times 10^{-5}$ T ≈ 3.14 × 10⁻⁵ T',
      },
      solution: {
        english: '$B = \\mu_0 I/(2R) = 4\\pi\\times 10^{-7}\\cdot 5/(0.2) = \\pi\\times 10^{-5}$.',
        hindi: '$B = \\mu_0 I/(2R) = 4\\pi\\times 10^{-7}\\cdot 5/(0.2) = \\pi\\times 10^{-5}$.',
      },
    },
    {
      year: 2020,
      exam: 'NEET',
      tag: 'Torque',
      problem: {
        english: 'Torque on a square coil (side 0.1 m, 100 turns) carrying 2 A in 0.5 T field, plane parallel to field.',
        hindi: '0.5 टी क्षेत्र में 2 ए ले जाने वाली एक वर्गाकार कुंडली (पक्ष 0.1 मीटर, 100 मोड़) पर टॉर्क, क्षेत्र के समानांतर समतल।',
      },
      answer: {
        english: '$1$ N·m',
        hindi: '$1$ N·m',
      },
      solution: {
        english: '$\\tau = NIAB\\sin 90° = 100 \\cdot 2\\cdot 0.01\\cdot 0.5 = 1$ N·m.',
        hindi: '$\\tau = एनआईएबी\\sin 90° = 100 \\cdot 2\\cdot 0.01\\cdot 0.5 = 1$ N·m.',
      },
    },
  ],
  next: {
    slug: 'magnetism-matter',
    title: 'Magnetism & Matter',
  },
};

export default chapter;
